import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "@/shared/api/httpClient";
import { toast } from "@/shared/hooks/use-toast";

import { acqService, type AcqDiscoverRunView } from "../api/acqService";
import {
  CONTENT_ZERO_TOAST,
  explainZeroRun,
  explainZeroRunByFunnel,
  isContentArchetype,
} from "./acqLabels";
import { errorToast } from "@/shared/lib/apiErrors";
import { todayFullReasonOf } from "./todayFull";

/** 跑一轮时的可选项:围绕哪个产品跑 + 这条线是什么原型(决定 0 张时怎么说)。 */
export interface DiscoverRunOptions {
  /** 契约 §16.2:选中了产品页签就带上它,本轮只用这个产品的词组。空串 = 不带。 */
  productCode?: string;
  /** 契约 §16.4:`question / comment / search` 三个原型的 0 张**不是失败**。 */
  archetype?: string | null;
}

/** 轮询间隔。一轮 3~6 分钟,4 秒一问既不让人干等,也不把接口打成心跳。 */
export const DISCOVER_POLL_MS = 4_000;

/**
 * 轮询上限 10 分钟。后端判死线是 15 分钟,排在它之后:前端先停下说「还没跑完」,后端那边真跑完了会把
 * 「上次」写到卡上;真死了由后端判死 —— 两边各自只说自己知道的那一半。
 */
export const DISCOVER_POLL_MAX_MS = 10 * 60_000;

/** 按钮上的「已等 m:ss」。 */
export function formatElapsed(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

interface Tracking {
  runId: string;
  type: string;
  /** 本机时间轴上的起点。发起时 = `Date.now()`;接上一轮在跑的时 = `Date.now() - elapsedMs`(服务端算的已用时)。 */
  startedAtMs: number;
}

/**
 * 跑一轮发现(契约 §12.4 `POST /acq/discover/run`,body `{type}` 必填)。
 *
 * ★ 0927 异步化:发起立即拿 `runId`,然后每 4 秒读一次 `GET /acq/discover/runs/{id}` 直到终态。
 *   此前是同步等 3~6 分钟,网关 504 之后靠「每 20s 刷目录、最多 12 次」盲兜 —— 用户四分钟里看不到
 *   任何原因文案,超过四分钟页面就不再动了。那套兜底连同它的注释一起删了:**不再有「504 = 后台还在跑」这一支**,
 *   发起口现在只会当场 4xx 或立即返回。
 * ★ 页面刷新后若卡片仍 `RUNNING`,`resume` 接上同一轮的轮询(起点用服务端的 `elapsedMs`)—— 一轮正在花钱的采集
 *   不该因为刷新在面上消失。
 * ★★ 扣没扣由服务端的 `charged` 说了算,不由"有没有结果"在前端推:
 *   0 张卡后端不 settle(契约 §12.10),但**判据在后端**。前端自己推一遍就是第二份真相,
 *   两边哪天不一致,面上会对一次真扣了的动作说「没扣分」。
 * ★ 积分数用服务端的 `credits`,**永不硬编 180/80**。
 */
export function useDiscoverRun(onRefresh: () => unknown) {
  const [runningType, setRunningType] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  /**
   * 1006:api 拒绝「今日商机已满」时的那句人话。商机上限按租户算,满了每条付费线都进不了今日页 ——
   * 页面据此把付费线的「发现一次」一并禁用并在按钮旁说原因(第一方 0 价线后端不判,不禁)。
   */
  const [todayFullReason, setTodayFullReason] = useState<string | null>(null);
  /** 正在跟的那一轮。同一时间只跟一轮:后端同卡也只放一轮(409)。 */
  const tracking = useRef<Tracking | null>(null);
  /** 组件还挂着吗。卸载后轮询要停:一个已经没人看的页面不该还在每 4s 打接口、更不该再弹 toast。 */
  const alive = useRef(true);
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null);
  const sleeper = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopTimers = useCallback(() => {
    if (ticker.current) clearInterval(ticker.current);
    ticker.current = null;
    if (sleeper.current) clearTimeout(sleeper.current);
    sleeper.current = null;
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      stopTimers();
    };
  }, [stopTimers]);

  /** 可被卸载打断的等待:卸载时 clearTimeout,这个 promise 就永远不 resolve,轮询循环随之停在原地。 */
  const sleep = useCallback(
    (ms: number) =>
      new Promise<void>((resolve) => {
        sleeper.current = setTimeout(() => {
          sleeper.current = null;
          resolve();
        }, ms);
      }),
    []
  );

  /** 终态怎么说。返回「有没有出单」(页面据此决定跳不跳今日商机)。 */
  const announce = useCallback((v: AcqDiscoverRunView, opts: DiscoverRunOptions): boolean => {
    if (v.status === "FAILED") {
      // 后端写的是一句人话(上游不可用 / 中途出错 / 等太久判死),原样露出。
      // ★ 标题不写「没扣分」:判死那一支后端自己都不敢承诺,前端更不能替它说。
      toast({
        title: "这轮没跑完",
        description: v.error ?? "后台没有给出原因，稍后再发起一次",
        variant: "destructive",
      });
      return false;
    }
    const r = v.result;
    if (!r) {
      toast({ title: "这轮跑完了，但没拿到回执", description: "刷新目录看这张卡上的「上次」" });
      return false;
    }
    const got = r.inserted + r.updated;
    /**
     * ★★ 内容线(知乎提问 / 评论区 / 搜一搜)的 0 张**不是失败**(契约 §16.4):
     *   它们抓的是"现在就该回答的问题",出口是一篇回答,本来就不产出"一条线索"。
     *   对它们说「换个词或者换个地方找找」,等于劝用户把一条正在正常工作的线关掉。
     * ★ 只换**没扣钱**那一支的话:扣了钱的那一支说的是账,两条线一样。
     */
    const contentLine = isContentArchetype(opts.archetype);
    // 0925:有漏斗先按漏斗说(没抓到 / 抓到了都不是客户);判读没跑通、老后端没漏斗时
    // 回落 0923 的原因分布那句;连计数都没有就沿用下面的旧文案
    const why =
      explainZeroRunByFunnel(r.funnel, r.rejectBreakdown) ??
      explainZeroRun(r.rawCount, r.rejectBreakdown);
    toast(
      r.charged
        ? {
            title: `扣 ${r.credits} 积分，新进 ${r.inserted} 条`,
            description: `看了 ${r.rawCount} 条原始信号，精判留下 ${r.judgedCount} 条${
              r.updated > 0 ? `，另有 ${r.updated} 条已在库里(刷新了信息)` : ""
            }${heldByColleagueNote(r.heldByColleague)}${r.signalDictMissing ? "（该行业还没铸 007 信号词典，按通用判据跑的）" : ""}`,
          }
        : contentLine
          ? {
              title: CONTENT_ZERO_TOAST,
              description:
                why ??
                `看了 ${r.rawCount} 条原始信号，这轮没有正好能接上话的。这条线按内容算账，不按条数算。`,
            }
          : {
              // ★ 这一句是产品承诺的落点:0 张卡不扣。说得越明白,用户越敢点下一次。
              title: "这轮没发现，没扣分",
              description:
                why ?? `看了 ${r.rawCount} 条原始信号，没有一条够格。换个词或者换个地方找找。`,
            }
    );
    return got > 0;
  }, []);

  /**
   * 跟一轮直到终态 / 超时 / 卸载。第一拍**立刻**问(第一方线几秒就完),之后每 4 秒一拍。
   * 网络抖一下不算失败(后端还在跑,下一拍再问);404 = 记录没了,停下说清。
   */
  const track = useCallback(
    async (t: Tracking, opts: DiscoverRunOptions): Promise<boolean> => {
      tracking.current = t;
      setRunningType(t.type);
      const tick = () => setElapsedSec(Math.floor((Date.now() - t.startedAtMs) / 1000));
      tick();
      ticker.current = setInterval(tick, 1_000);
      try {
        let first = true;
        for (;;) {
          if (!first) await sleep(DISCOVER_POLL_MS);
          first = false;
          if (!alive.current) return false;
          if (Date.now() - t.startedAtMs > DISCOVER_POLL_MAX_MS) {
            toast({
              title: "还没跑完",
              description:
                "已经等了 10 分钟。后台还在找，跑完后这张卡的「上次」会更新，稍后刷新看看",
            });
            await onRefresh();
            return false;
          }
          let v: AcqDiscoverRunView;
          try {
            v = await acqService.discoverRun(t.runId);
          } catch (e) {
            if (e instanceof ApiError && e.httpStatus === 404) {
              toast({
                title: "找不到这一轮的记录",
                description: "刷新目录看这张卡上的「上次」",
                variant: "destructive",
              });
              await onRefresh();
              return false;
            }
            continue; // 网络抖动:下一拍再问
          }
          if (!alive.current) return false;
          if (v.status !== "RUNNING") {
            const got = announce(v, opts);
            await onRefresh();
            return got;
          }
        }
      } finally {
        stopTimers();
        tracking.current = null;
        if (alive.current) {
          setRunningType(null);
          setElapsedSec(0);
        }
      }
    },
    [announce, onRefresh, sleep, stopTimers]
  );

  const run = useCallback(
    async (type: string, opts: DiscoverRunOptions = {}): Promise<boolean> => {
      if (tracking.current) {
        toast({ title: "上一轮还在找", description: "等它跑完再点" });
        return false;
      }
      setRunningType(type);
      let runId: string;
      try {
        const started = await acqService.runDiscover(type, opts.productCode || undefined);
        runId = started.runId;
      } catch (e) {
        // 发不起来 = 什么都没发生(三道闸在后端请求线程上,409 同卡在跑 / 429 池满也落这里)
        if (alive.current) setRunningType(null);
        const full = todayFullReasonOf(e);
        if (full) {
          if (alive.current) setTodayFullReason(full);
          // api 契约:toast 直接显示 api message
          toast({ title: full, description: "这次没扣积分" });
          void onRefresh(); // 页面据此重拉今日条数,卡上的禁用随之落到「total >= cap」口径
          return false;
        }
        toast(errorToast(e, { fallbackTitle: "这轮没跑起来，没扣分" }));
        return false;
      }
      if (!alive.current) return false;
      return track({ runId, type, startedAtMs: Date.now() }, opts);
    },
    [track, onRefresh]
  );

  /**
   * 接上一轮已经在跑的(页面刷新 / 别处发起的)。不重新 POST、不跳页 —— 只是把面上的「正在找」和终态提示接回来。
   * 已经在跟别的轮或这轮不在跑时什么都不做。
   */
  const resume = useCallback(
    (type: string, view: AcqDiscoverRunView, opts: DiscoverRunOptions = {}) => {
      if (tracking.current || view.status !== "RUNNING") return;
      void track(
        { runId: view.runId, type, startedAtMs: Date.now() - (view.elapsedMs ?? 0) },
        opts
      );
    },
    [track]
  );

  return { runningType, elapsedSec, run, resume, todayFullReason };
}

/**
 * 1027 归属分权:这一轮扫到了几张**同事名下**的卡。卡归原主、我在列表里看不到,
 * 不说出来的话用户会以为「扣了分、刷新了 3 条,线索库里却一条都没变」。只报个数不给内容。
 */
export function heldByColleagueNote(n: number | undefined): string {
  return n && n > 0 ? `，其中 ${n} 条已在同事名下` : "";
}
